import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { formatNumber } from '@progress/kendo-intl';
import { fetchApi } from 'services/api';
import LoadingPanel from 'components/LoadingPanel';

type CompMonth = {
  Name: string;
  Goal: number;
  Actual: number;
  AttainmentPct: number;
  Paid: number;
  Earned: number;
}

type CompAccount = {
  CustomerName: string;
  Revenue: number;
  Tier: number;
  Payout: number;
  WindowClosed: boolean;
}

type Compensation = {
  PlanName: string;
  QuarterName: string;
  Element1Paid: number;
  Element1Earned: number;
  Element1TrueUp: number;
  Element1TrueUpUnlocked: boolean;
  Element1AnnualAttainmentPct: number;
  Element1AttainmentPct: number;
  Element1Aggregate: boolean;
  Element1Months: CompMonth[];
  Element2Payout: number;
  Element2QualifyingAccounts: number;
  Element2GateCleared: boolean;
  Element2Accounts: CompAccount[];
  Element3Type: string;
  Element3Mode: string;
  Element3Applicable: boolean;
  Element3Pending: boolean;
  Element3Payout: number;
  Element3AttainmentPct: number;
  Element3GateCleared: boolean;
  Element3Revenue: number;
  Element3Pay: number;
  Element3Margin: number;
  QuarterTotal: number;
}

type Response = {
  Compensation: Compensation | null;
}

type Props = {
  salespersonId?: string;
  active: boolean;
}

const attainmentClass = (fraction: number, floor: number) =>
  fraction >= 1 ? 'text-success' : fraction >= floor ? 'text-warning' : 'text-danger';

const CompensationTab = ({ salespersonId, active }: Props) => {
  const [compensation, setCompensation] = useState<Compensation | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const fetchCompensation = useCallback(() => {
    setLoading(true);
    fetchApi(`/api/Customer/SalespersonCompensation/${salespersonId}`, undefined, 'GET')
      .then((response: Response) => {
        setCompensation(response.Compensation ?? null);
        setFetched(true);
      })
      .catch((e) => {
        if (!e?.status) alert('Error fetching compensation');
      })
      .finally(() => setLoading(false));
  }, [salespersonId]);

  // Reset cache when the salesperson changes so a re-open refetches for the new rep.
  useEffect(() => {
    setFetched(false);
    setCompensation(null);
  }, [salespersonId]);

  // Lazy: fetch the first time the tab is opened (and after a salesperson change).
  useEffect(() => {
    if (active && !fetched && !loading) fetchCompensation();
  }, [active, fetched, loading, fetchCompensation]);

  if (loading) {
    return <LoadingPanel />;
  }

  if (fetched && !compensation) {
    return <p className="text-muted my-3">No 2026 comp plan is configured for this salesperson.</p>;
  }

  if (!compensation) {
    return null;
  }

  return <React.Fragment>
    <h5 className="mb-1">{compensation.QuarterName}</h5>
    <p className="text-muted mb-2">{compensation.PlanName}</p>
    <div className="row">
      <div className="col-sm-6 col-md-4 col-lg-3 mt-2">
        <div className="card h-100">
          <div className="card-body">
            <h5 className="text-muted fw-normal mt-0">{compensation.QuarterName} Est. Total Payout</h5>
            <h3 className="mt-2 mb-2">{formatNumber(compensation.QuarterTotal, 'c')}</h3>
            {compensation.Element1TrueUp > 0 && <p className="mb-0 text-muted">+ {formatNumber(compensation.Element1TrueUp, 'c')} true-up if year finishes ≥100%</p>}
          </div>
        </div>
      </div>
      <div className="col-sm-6 col-md-4 col-lg-3 mt-2">
        <div className="card h-100">
          <div className="card-body">
            <h5 className="text-muted fw-normal mt-0">Element 1 — Individual</h5>
            <h3 className="mt-2 mb-2">{formatNumber(compensation.Element1Paid, 'c')}</h3>
            <p className="mb-1">Attainment <span className={attainmentClass(compensation.Element1AttainmentPct, 0.85)}>{formatNumber(compensation.Element1AttainmentPct, 'p1')}</span></p>
            {compensation.Element1TrueUp > 0 && <p className="mb-1">
              {formatNumber(compensation.Element1TrueUp, 'c')} true-up (YTD) ·{' '}
              {compensation.Element1TrueUpUnlocked
                ? <span className="text-success">on pace ✓ {formatNumber(compensation.Element1AnnualAttainmentPct, 'p1')} YTD</span>
                : <span className="text-danger">locked · {formatNumber(compensation.Element1AnnualAttainmentPct, 'p1')} YTD (needs 100%)</span>}
            </p>}
            {compensation.Element1Aggregate && <p className="mb-0 text-muted">Quarterly aggregate</p>}
          </div>
        </div>
      </div>
      <div className="col-sm-6 col-md-4 col-lg-3 mt-2">
        <div className="card h-100">
          <div className="card-body">
            <h5 className="text-muted fw-normal mt-0">Element 2 — New Accounts</h5>
            <h3 className="mt-2 mb-2">{formatNumber(compensation.Element2Payout, 'c')}</h3>
            <p className="mb-1">{compensation.Element2QualifyingAccounts} qualifying account{compensation.Element2QualifyingAccounts === 1 ? '' : 's'}</p>
            <p className="mb-0">{compensation.Element2GateCleared
              ? <span className="text-success">✓ 70% E1 gate cleared</span>
              : <span className="text-danger">✗ Below 70% E1 gate</span>}</p>
          </div>
        </div>
      </div>
      <div className="col-sm-6 col-md-4 col-lg-3 mt-2">
        <div className="card h-100">
          <div className="card-body">
            <h5 className="text-muted fw-normal mt-0">Element 3 — Territory / LOGO</h5>
            {compensation.Element3Pending
              ? <React.Fragment><h3 className="mt-2 mb-2 text-muted">Pending</h3><p className="mb-0 text-muted">{compensation.Element3Type}</p></React.Fragment>
              : compensation.Element3Mode === 'Territory'
                ? <React.Fragment>
                    <h3 className="mt-2 mb-2">{formatNumber(compensation.Element3Payout, 'c')}</h3>
                    <p className="mb-1">Territory attainment <span className={attainmentClass(compensation.Element3AttainmentPct, 0.85)}>{formatNumber(compensation.Element3AttainmentPct, 'p1')}</span></p>
                    <p className="mb-0">{compensation.Element3GateCleared
                      ? <span className="text-success">✓ E1 gate cleared</span>
                      : <span className="text-danger">✗ E1 gate not met</span>}</p>
                  </React.Fragment>
                : compensation.Element3Mode === 'NewLogo'
                  ? <React.Fragment>
                      <h3 className="mt-2 mb-2">{formatNumber(compensation.Element3Payout, 'c')}</h3>
                      <p className="mb-1">2% of {formatNumber(compensation.Element3Margin, 'c0')} GM</p>
                      <p className="mb-0 text-muted">{formatNumber(compensation.Element3Revenue, 'c0')} Revenue</p>
                      <p className="mb-0 text-muted">{formatNumber(compensation.Element3Pay, 'c0')} Cost</p>
                    </React.Fragment>
                  : <React.Fragment><h3 className="mt-2 mb-2 text-muted">—</h3><p className="mb-0 text-muted">No Element 3 on this plan</p></React.Fragment>}
          </div>
        </div>
      </div>
    </div>
    {compensation.Element1Months.length > 0 && <div className="mt-3">
      <h6 className="text-muted">Element 1 — Monthly Detail</h6>
      <table className="table table-sm">
        <thead><tr><th>Month</th><th className="text-right">Goal</th><th className="text-right">Actual</th><th className="text-right">Attainment</th><th className="text-right">Paid</th></tr></thead>
        <tbody>
          {compensation.Element1Months.map((m, i) => <tr key={i}>
            <td>{m.Name}</td>
            <td className="text-right">{formatNumber(m.Goal, 'c0')}</td>
            <td className="text-right">{formatNumber(m.Actual, 'c0')}</td>
            <td className={`text-right ${attainmentClass(m.AttainmentPct, 0.85)}`}>{formatNumber(m.AttainmentPct, 'p1')}</td>
            <td className="text-right">{formatNumber(m.Paid, 'c')}</td>
          </tr>)}
        </tbody>
      </table>
    </div>}
    {compensation.Element2Accounts.length > 0 && <div className="mt-2">
      <h6 className="text-muted">Element 2 — Qualifying New Accounts (first 90 days)</h6>
      <table className="table table-sm">
        <thead><tr><th>Account</th><th className="text-right">90-Day Revenue</th><th className="text-right">Tier</th><th className="text-right">Payout</th><th>Window</th></tr></thead>
        <tbody>
          {compensation.Element2Accounts.map((a, i) => <tr key={i}>
            <td>{a.CustomerName}</td>
            <td className="text-right">{formatNumber(a.Revenue, 'c0')}</td>
            <td className="text-right">T{a.Tier}</td>
            <td className="text-right">{a.WindowClosed ? formatNumber(a.Payout, 'c') : <span className="text-muted">{formatNumber(a.Payout, 'c')}</span>}</td>
            <td>{a.WindowClosed ? <span className="text-muted">Closed</span> : <span className="text-success">Open</span>}</td>
          </tr>)}
        </tbody>
      </table>
    </div>}
  </React.Fragment>;
};

export default CompensationTab;
